import {Platform, StyleSheet, useWindowDimensions, View} from "react-native";
import {useRef, useState} from "react";
import {Image} from "expo-image";
import {CropZoom, type CropZoomRefType, SizeVector, useImageResolution} from "react-native-zoom-toolkit";
import {Color} from "@/lib/styles";
import ActionButton from "@/lib/components/ActionButton";
import {CropRequest, CropResponse, ThumbnailSize, UploadResponse} from "@/lib/types";
import {fetchBackend} from "@/lib/backend";
import {useSession} from "@/lib/auth/auth";
import SvgCircleMask from "@/lib/components/SvgCircleMask";
import SvgRectMask from "@/lib/components/SvgRectMask";
import ImageUpload from "@/lib/components/ImageUpload";
import SpinnerPage from "@/lib/pages/SpinnerPage";
import {useSafeAreaInsets} from "react-native-safe-area-context";

type Props = {
    source?: UploadResponse,
    sizeType: ThumbnailSize,
    onCrop: (response: CropResponse) => void,
};

export default function Cropper({source, sizeType, onCrop}: Props) {
    const ref = useRef<CropZoomRefType>(null);
    const {session} = useSession();
    const [cropping, setCropping] = useState(false);
    const [imageSource, setImageSource] = useState(source);
    const inset = useSafeAreaInsets();

    const {isFetching, resolution} = useImageResolution({uri: imageSource ? imageSource.image_url : ''});
    const {width, height} = useWindowDimensions();

    if (isFetching) {
        return <SpinnerPage/>;
    }

    if (!imageSource || !resolution) {
        return (
            <ImageUpload onUpload={onUpload}/>
        )
    }

    function onUpload(response: UploadResponse) {
        setImageSource(response);
    }

    // Twice as large as the final result
    const CARD_DIM = 1920;
    const AVATAR_DIM = 576;

    function buildCropRequest(): CropRequest {
        const state = ref?.current?.getState()!;

        const ratioX = resolution!.width / state.childSize.width;
        const ratioY = resolution!.height / state.childSize.height;

        const topInChild =
            (state.childSize.height / 2)
            - (cropSize.height / 2) / state.scale
            - (state.translateY / state.scale)

        const leftInChild =
            (state.childSize.width / 2)
            - (cropSize.width / 2) / state.scale
            - (state.translateX / state.scale)

        // Scale to target size
        const minRes = Math.min(resolution!.width, resolution!.height);
        const scaleFactor = (ThumbnailSize.CARD === sizeType
                ? CARD_DIM / resolution!.width
                : AVATAR_DIM * 0.5 / minRes
        );

        return {
            scale: state.scale * scaleFactor,
            picture_id: imageSource!.picture_id,
            size_type: sizeType,
            y: Math.round(topInChild * ratioY),
            x: Math.round(leftInChild * ratioX),
        }
    }

    function getCropSize(): SizeVector<number> {
        if (sizeType === ThumbnailSize.CARD) {
            return {
                width,
                height: width / 2.5,
            }
        } else if (sizeType === ThumbnailSize.AVATAR) {
            let sz = Math.min(width, AVATAR_DIM);
            return {
                width: sz,
                height: sz,
            }
        } else {
            throw "Undefined size type: " + sizeType
        }
    }

    const cropSize = getCropSize();

    async function runCropApi() {
        setCropping(true);
        const payload = buildCropRequest();
        const response = await fetchBackend<CropResponse>("/picture/crop", "POST", session, payload);
        setCropping(false);
        onCrop(response);
    }

    async function zoomBy(factor: number) {
        const state = ref?.current?.getState()!;
        ref?.current?.setTransformState({
            scale: state.scale * factor,
            translateX: state.translateX * factor,
            translateY: state.translateY * factor,
            rotate: 0,
            rotateX: 0,
            rotateY: 0,
        }, false);
    }

    async function zoomIn() {
        await zoomBy(1.2);
    }

    async function zoomOut() {
        await zoomBy(1 / 1.2);
    }

    const controlsOffset = (height / 2) - (cropSize.height / 2) - inset.top - 16;

    return (
        <View style={styles.root}>
            <CropZoom ref={ref}
                      cropSize={cropSize}
                      resolution={resolution!}
                      maxScale={20}
                      minScale={1}
                      allowPinchPanning={true}
                      OverlayComponent={() => (
                          <View style={[styles.overlay, {width, height}]}>
                              {sizeType === ThumbnailSize.AVATAR && <SvgCircleMask radius={cropSize.width / 2}/>}
                              {sizeType === ThumbnailSize.CARD && <SvgRectMask width={cropSize.width}
                                                                               height={cropSize.height}
                                                                               x={(width / 2) - (cropSize.width / 2)}
                                                                               y={(height / 2) - (cropSize.height / 2)}
                              />}
                          </View>
                      )}
                      panMode={"free"}
                      scaleMode={"bounce"}
            >
                <Image source={imageSource!.image_url} style={styles.image}/>
            </CropZoom>
            <View style={[styles.controls, {top: controlsOffset}]}>
                <ActionButton icon={"camera"} onPress={async () => {
                    setImageSource(undefined)
                }}/>
                {Platform.OS === "web" && <ActionButton title={"-"} onPress={zoomOut}/>}
                {Platform.OS === "web" && <ActionButton title={"+"} onPress={zoomIn}/>}
                <ActionButton title={"Bruk"} icon={"checkmark"} busy={cropping} onPress={runCropApi}
                              color={Color.Green}/>
            </View>
        </View>
    )
}

const styles = StyleSheet.create({
    root: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: Color.Background,
    },
    image: {
        width: "100%",
        height: "100%",
    },
    controls: {
        position: 'absolute',
        width: '100%',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'flex-end',
        paddingRight: 8,
    },
    overlay: {
        width: "100%",
        height: "100%",
    },
    mask: {
        width: "100%",
        backgroundColor: '#00000077',
    },
});